"use client";

import {
  ReactNode,
  Children,
  Fragment,
  isValidElement,
  type ReactElement,
} from "react";
import { Button } from "@/components/button";

interface SearchContainerProps {
  children: ReactNode;
  onSearch?: () => void;
  searchLabel?: string;
  className?: string;
  isSearchEnabled?: boolean;
  isLoading?: boolean;
  searchBox?: string;
}

type FieldProps = {
  label?: string;
  placeholder?: string;
  "aria-label"?: string;
};

function getFieldKey(child: ReactNode): string {
  if (!isValidElement(child)) {
    return "search-field";
  }

  const element = child as ReactElement<FieldProps>;
  const props = element.props;

  return (
    props.label ??
    props.placeholder ??
    props["aria-label"] ??
    (typeof element.type === "string" ? element.type : element.type.name) ??
    "search-field"
  );
}

export default function SearchContainer({
  children,
  onSearch,
  searchLabel = "جستجو",
  className = "",
  isSearchEnabled = false,
  isLoading = false,
  searchBox = "با وارد کردن عنوان آگهی یا با استفاده از فیلترهای زیر (نوع معامله و نوع آگهی الزامی است) جستجو کنید.",
}: Readonly<SearchContainerProps>) {
  const items = Children.toArray(children);

  return (
    <div className="flex flex-col gap-5 w-full max-w-6xl">
      <p className="text-ordinary text-base font-bold text-center px-2">
        {searchBox}
      </p>

      <div
        dir="rtl"
        className={`
          w-full bg-[#F5F6F8]
          border border-gray-300
          rounded-2xl md:rounded-full
          px-4 py-3 md:py-2
          flex flex-col md:flex-row md:items-center
          gap-4 md:gap-2
          ${className}
        `}
      >
        {/* Equal-width slots for every rendered field (3 for customer, 4 when area is present) */}
        <div className="flex-1 min-w-0 flex flex-col md:flex-row items-stretch md:items-center gap-4 md:gap-0">
          {items.map((child, index) => (
            <Fragment key={getFieldKey(child)}>
              <div className="w-full flex-1 min-w-0 md:px-3">
                {child}
              </div>

              {index !== items.length - 1 && (
                <span
                  aria-hidden
                  className="hidden md:block w-px h-6 bg-brand shrink-0"
                />
              )}
            </Fragment>
          ))}
        </div>

        <Button
          variant="bare"
          type="button"
          text={isLoading ? "در حال جستجو" : searchLabel}
          onClick={onSearch}
          disabled={!isSearchEnabled || isLoading}
          iconPosition="end"
          iconNode={
            isLoading ? (
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : undefined
          }
          className={`
            hover:cursor-pointer
            relative w-full mt-1 md:mt-0 md:w-auto md:shrink-0
            px-8 py-3.5 rounded-xl md:rounded-full
            font-medium
            transition-all duration-200
            overflow-hidden
            ${!isSearchEnabled || isLoading
              ? "bg-gray-400 text-gray-200"
              : "bg-[#4B57E3] text-white hover:bg-[#3d47c0] hover:shadow-md"
            }
          `}
        />
      </div>
    </div>
  );
}
